import { ApplicantReminderEmailTemplates, EmailTemplates } from '../emails/index.js';
import {
  //ApplicantOfferRequirementTask,
  Either,
  IImportCourse,
  IImportData,
  IImportOffer,
  IntakeStatus,
  IRecordedInterviewQuestion,
  ITag,
  SMapConditionGroup,
  SMapWhen,
} from '../index.js';
import { CoreStatus, CoreStatusExtended } from '../offer/index.js';
import { IUserRole, PathwayAccess } from '../user/index.js';

import { IApplyUniConfig } from '../apply/config/index.js';
import { IRegionConfig } from '../region-config/index.js';

export * from './types.js';

export type ISystemAndUniConfig = ISystemConfig & IUniConfig;

/** Response shape for the get-pathway-sys-config endpoint. */
export interface IPathwaySysConfigResponse {
  config: Partial<ISystemAndUniConfig> & { agentDetailsEnabled: boolean; agentCompanyNameForStudentEnabled: boolean };
  regionConfig: IRegionConfig;
}

/** Response shape for the get-uni-sys-config endpoint. */
export interface IUniSysConfigResponse extends IPathwaySysConfigResponse {
  applyConfig: IApplyUniConfig | null;
}
export type ISystemAndImmiConfig = ISystemConfig & IImmigrationProviderConfig;

export type IConfigForUniAdmin = Omit<ISystemAndUniConfig, 'sponsor' | 'universityIds' | 'urls' | 'CalendlyConfig'>;
export type IConfigForEnroly = Omit<ISystemAndUniConfig, 'sftpAdminEmail' | 'maskedSftpPubKey' | 'sponsor'>;
export type IConfigForUniAndUniSecure = Omit<
  ISystemAndUniConfig,
  | 'sftpAdminEmail'
  | 'maskedSftpPubKey'
  | 'accessToken'
  | 'setupTypes'
  | 'formatTypes'
  | 'importFieldMappings'
  | 'sftpImportFieldMappings'
  | 'sponsor'
  | 'documentDownloadsActive'
  | 'visaStatuses'
  | 'universityIds'
  | 'urls'
  | 'contactUniEmails'
  | 'conflictReportEmails'
  | 'dataEscrowConfig'
  | 'CalendlyConfig'
  | 'sits'
>;
export type IConfigForUniLiteAndAgent = Omit<
  ISystemAndUniConfig,
  | 'sftpAdminEmail'
  | 'maskedSftpPubKey'
  | 'accessToken'
  | 'setupTypes'
  | 'formatTypes'
  | 'importFieldMappings'
  | 'sftpImportFieldMappings'
  | 'sponsor'
  | 'documentDownloadsActive'
  | 'stages'
  | 'visaStatuses'
  | 'universityIds'
  | 'urls'
  | 'contactUniEmails'
  | 'conflictReportEmails'
  | 'dataEscrowConfig'
  | 'CalendlyConfig'
  | 'sits'
>;

export type IConfigForPartners = Omit<
  ISystemAndUniConfig,
  | 'sftpAdminEmail'
  | 'maskedSftpPubKey'
  | 'accessToken'
  | 'setupTypes'
  | 'formatTypes'
  | 'importFieldMappings'
  | 'sftpImportFieldMappings'
  | 'sponsor'
  | 'documentDownloadsActive'
  | 'stages'
  | 'visaStatuses'
  | 'universityIds'
  | 'urls'
  | 'contactUniEmails'
  | 'conflictReportEmails'
  | 'dataEscrowConfig'
  | 'CalendlyConfig'
  | 'sits'
>;
export type IConfigForImmigrationProviders = Omit<
  ISystemAndUniConfig,
  | 'sftpAdminEmail'
  | 'maskedSftpPubKey'
  | 'accessToken'
  | 'setupTypes'
  | 'formatTypes'
  | 'importFieldMappings'
  | 'sftpImportFieldMappings'
  | 'sponsor'
  | 'stages'
  | 'visaStatuses'
  | 'universityIds'
  | 'urls'
  | 'contactUniEmails'
  | 'conflictReportEmails'
  | 'dataEscrowConfig'
  | 'CalendlyConfig'
  | 'sits'
>;
export type IConfigDefault = Omit<
  ISystemAndUniConfig,
  | 'dashboardTabs'
  | 'mappingTypes'
  | 'apiEndpointUrl'
  | 'accessKey'
  | 'documentDownloads'
  | 'sftpAdminEmail'
  | 'maskedSftpPubKey'
  | 'accessToken'
  | 'roles'
  | 'setupTypes'
  | 'formatTypes'
  | 'importFieldMappings'
  | 'sftpImportFieldMappings'
  | 'sponsor'
  | 'intakeIds'
  | 'defaultIntakeId'
  | 'universityIds'
  | 'defaultUniversityId'
  | 'documentDownloadsActive'
  | 'stages'
  | 'moveToStages'
  | 'visaStatuses'
  | 'urls'
  | 'contactUniEmails'
  | 'conflictReportEmails'
  | 'dataEscrowConfig'
  | 'CalendlyConfig'
  | 'sits'
>;

export interface IRole {
  label: string;
  value: string;
}

export interface IUniId {
  fullName: string;
  label: string;
  value: string;
  isPathway?: boolean;
}
export interface IPartnerId {
  fullName: string;
  label: string;
  value: string;
}
export interface IHydratePathwayUniAssets {
  [key: string]: {
    student: string;
  };
}

export interface IPathwayProviderPathwayUni extends IUniId {
  hydratePathwayUnis: IHydratePathwayUniAssets;
  isPathway: boolean;
}

export enum MaxPathwayAccess {
  Full = 'Full',
  Partial = 'Partial',
}

export interface IPathwayId {
  label: string;
  value: string;
  maxPathwayAccess?: MaxPathwayAccess | string;
  readyForCASEmailStatus?: ConfigEmailStatus;
  deferralNotificationEmailStatus?: ConfigEmailStatus;
  newInterviewRequiredStatus?: ConfigEmailStatus;
  addAndCancelInterviewVisible?: boolean;
  scheduledInterviewsVisible?: boolean; // true by default
  recordedInterviewVisible?: boolean; // true by default
  recordedInterviewJobId?: string;
}

export interface ISystemConfig {
  apiEndpointUrl: string;
  roles: Array<IRole>;
  universityIds: Array<IUniId>;
  partnerIds: Array<IPartnerId>;
  allPartners: Array<string>;
  allImmigrationProviders: Array<string>;
  urls: IUrls;
  enrolyAccess: string;
  admAllowedAliases?: Array<string>;
  // TODO: trapQuestions: Array<IRecordedInterviewQuestion>; // present in db but missing here
}

export interface IUrl {
  appClient?: string;
  uni?: string;
  userpool?: string;
  cognitoDomain?: string;
  apply?: string;
  staffAppClient?: string;
  student?: string;
  studentAppClient?: string;
  userKey?: string;
  staffMFAEnabled?: boolean;
  source?: string;
  applySource?: string;
  sourceARN?: string;
  replyTo?: Array<string>;
  returnTo?: string;
  returnToARN?: string;
  idpLogout?: boolean;
  redirectUrl?: string;
  cognitoProviderName?: string;
  samlEnabled?: boolean;
}

export interface IUrls {
  [key: string]: IUrl | (IUrl & Record<string, IUrl>);
  ImmigrationProviders: Record<string, IUrl>;
  Partners: Record<string, IUrl>;
}

export interface IIntakeIdBase {
  label: string;
  value: string;
}

interface IIntakeIdSpecific {
  status: IntakeStatus;
  applyStatus?: IntakeStatus; // This might be temporary
  inviteWhen?: SMapWhen | string;
  inviteCondition?: SMapConditionGroup;
  associatedPathwayIntakes?: string[];
}

interface IIntakeIdWithDatesSpecific {
  startDate: string;
  endDate: string;
}

export interface IIntakeId extends IIntakeIdSpecific, IIntakeIdBase {}
export interface IIntakeIdWithDates extends IIntakeIdWithDatesSpecific, IIntakeIdBase {}

export type Intake = IIntakeIdBase & Either<IIntakeIdSpecific, IIntakeIdWithDatesSpecific>;

export enum TabCategories {
  NOT_INVITED = 'isNotInvited',
  INVITED = 'isInvited',
  SUBMITTED = 'isSubmitted',
  NOT_SUBMITTED = 'isNotSubmitted',
  ASSESSED = 'isAssessed',
}

type ITabMapped = {
  [category in TabCategories]?: boolean;
};

type ITabBase = {
  color?: string;
  id: string;
  isArrStatusVisible?: boolean;
  isAccStatusVisible?: boolean;
  isVisaStatusVisible?: boolean;
  isFollowUpDocsCountVisible?: boolean;
  isDocsCountVisible?: boolean;
  isAgentDocsCountVisible?: boolean;
  isConditionalStatusVisible?: boolean;
  isFundsMatVisible?: boolean;
  isDepositDateVisible?: boolean;
  isDaysInStageVisible?: boolean;
  isRecordedInterviewVisible?: boolean;
  isInterviewVisible?: boolean;
  label: string;
  statuses?: Array<string>;
  count?: number; // For dashboard
  width?: number;
  withIcon?: boolean;
};

export type ITab = ITabBase & ITabMapped;

export interface IFormatType {
  display?: string;
  shieldFormat: unknown;
  type: string;
  uniFormat: unknown;
}

export interface IFieldMap {
  format: string;
  mappingType?: string;
  regexStr?: string;
  defaultData?: string;
  momentFormat?: string;
  multiColumnFieldDelimeter?: string;
  setup: string;
  uniField: string;
  validationType?: string;
}

export interface IFieldMappingList {
  accomFeePaidToDate: IFieldMap;
  addressLine1: IFieldMap;
  addressLine2: IFieldMap;
  addressLine3: IFieldMap;
  agentCompany: IFieldMap;
  agentCompanyEmail: IFieldMap;
  agentEmail: IFieldMap;
  agentName: IFieldMap;
  agentOffice: IFieldMap;
  applicationDate: IFieldMap;
  applicationId: IFieldMap;
  branchOffice: IFieldMap;
  city: IFieldMap;
  confirmed: IFieldMap;
  counsellorEmail: IFieldMap;
  counsellorName: IFieldMap;
  country: IFieldMap;
  countryOfBirth: IFieldMap;
  courseFeePaidToDate: IFieldMap;
  courseProgression: IFieldMap;
  criminalConvictions: IFieldMap;
  dob: IFieldMap;
  docsUsedToObtainOffer: IFieldMap;
  email: IFieldMap;
  admissionsOffice: IFieldMap;
  englishLevelAttained: IFieldMap;
  englishTestProvider: IFieldMap;
  familyName: IFieldMap;
  gender: IFieldMap;
  givenName: IFieldMap;
  isAccomIncludedCourseFee: IFieldMap;
  isAccomProvided: IFieldMap;
  isSELTReqd: IFieldMap;
  isWorkPlacement: IFieldMap;
  mainStudyAddressCity: IFieldMap;
  mainStudyAddressCountry: IFieldMap;
  mainStudyAddressLine1: IFieldMap;
  mainStudyAddressLine2: IFieldMap;
  mainStudyAddressLine3: IFieldMap;
  mainStudyAddressPostcode: IFieldMap;
  nationality: IFieldMap;
  offers_courses_atas: IFieldMap;
  offers_courses_cah3: IFieldMap;
  offers_courses_conditional: IFieldMap;
  offers_courses_faculty: IFieldMap;
  offers_courses_declined: IFieldMap;
  offers_courses_conditions_text1: IFieldMap;
  offers_courses_conditions_text2: IFieldMap;
  offers_courses_conditions_text3: IFieldMap;
  offers_courses_conditions_text4: IFieldMap;
  offers_courses_conditions_text5: IFieldMap;
  offers_courses_conditions_text6: IFieldMap;
  offers_courses_conditions_text7: IFieldMap;
  offers_courses_conditions_text8: IFieldMap;
  offers_courses_conditions_text9: IFieldMap;
  offers_courses_end_date: IFieldMap;
  offers_courses_fee: IFieldMap;
  offers_courses_scholarshipAmount1: IFieldMap;
  offers_courses_scholarshipTitle1: IFieldMap;
  offers_courses_hoursPerWeek: IFieldMap;
  offers_courses_id: IFieldMap;
  offers_courses_inner_london: IFieldMap;
  offers_courses_isFullTime: IFieldMap;
  offers_courses_lastAcceptanceDate: IFieldMap;
  offers_courses_level: IFieldMap;
  offers_courses_secondaryLevel: IFieldMap;
  offers_courses_start_date: IFieldMap;
  offers_courses_title: IFieldMap;
  otherName: IFieldMap;
  passportExpiry: IFieldMap;
  passportNumber: IFieldMap;
  phone: IFieldMap;
  placeOfBirth: IFieldMap;
  postcode: IFieldMap;
  previousCourseCompletion: IFieldMap;
  previousCourseLevel: IFieldMap;
  prevUKStudy: IFieldMap;
  progressionText: IFieldMap;
  reasonSELTNotReqd: IFieldMap;
  seltListening: IFieldMap;
  seltReading: IFieldMap;
  seltSpeaking: IFieldMap;
  seltWriting: IFieldMap;
  studentNo: IFieldMap;
  ucasId: IFieldMap;
  withAgent: IFieldMap;
  workPlacementAddressCity: IFieldMap;
  workPlacementAddressCountry: IFieldMap;
  workPlacementAddressLine1: IFieldMap;
  workPlacementAddressLine2: IFieldMap;
  workPlacementAddressLine3: IFieldMap;
  workPlacementAddressPostcode: IFieldMap;
  workPlacementPerc: IFieldMap;
  workPlacementText: IFieldMap;
  offers_courses_offerEvidence: IFieldMap;
  offers_courses_accepted: IFieldMap;
  scholarshipAmount: IFieldMap;
  scholarshipTitle: IFieldMap;
  offers_courses_fee_discount: IFieldMap;
  agentFamilyName: IFieldMap;
  agentGivenName: IFieldMap;
  branchAddressLine1: IFieldMap;
  branchAddressLine2: IFieldMap;
  branchAddressLine3: IFieldMap;
  branchCity: IFieldMap;
  branchCountry: IFieldMap;
  branchPostcode: IFieldMap;
  uni?: IFieldMap;
  uniPartnerId?: IFieldMap;
  pathway?: IFieldMap;
}

export interface IMappingType {
  [name: string]: IMappingTypeValue<string> | IMappingTypeValue<number> | IMappingTypeValue<boolean>;
}
export interface IMappingTypeValue<T> {
  [name: string]: T;
}

export interface IStreakField {
  field: number;
  value: string;
}

export enum ConfigEmailStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export interface IDashboardTabs {
  '[Admin]': Array<ITab>;
  '[Agent]'?: Array<ITab>;
  '[Partner]'?: Array<ITab>;
  '[AgencyAdmin]'?: Array<ITab>;
  '[Enroly]'?: Array<ITab>;
  '[Uni]'?: Array<ITab>;
  '[UniAdmin]'?: Array<ITab>;
  '[UniLite]'?: Array<ITab>;
  '[UniSecure]'?: Array<ITab>;
  '[ImmigrationProvider]'?: Array<ITab>;
}

export interface IUniConfig {
  studentNumber?: {
    regex: string;
    error: string;
  };
  maskedSftpPubKey?: string;
  sftpAdminEmail?: string;

  sftpPull?: string;
  sftpPullKeyName?: string;
  sftpPullHost?: string;
  sftpPullPort?: string;
  sftpPullUserName?: string;
  sftpPullPostProcessingFolder?: string;
  sftpPullFolder?: string;

  registerTitleLine1?: string;
  registerTitleLine2?: string;
  accessKey: string;
  accessToken: string;
  allowedTagsLimit?: number;

  campuses: Array<{ name: string; code: string }>;
  faculties: Array<{ name: string; code: string }>;
  departments: Array<{ name: string; code: string }>;
  categories: Array<{ name: string; code: string }>;

  intakeIds: Array<IIntakeId>;
  visaStatuses: Array<string>;
  defaultUniversityId: string;
  dashboardTabs: IDashboardTabs;
  defaultQuestions: Array<IRecordedInterviewQuestion>;
  trapQuestions: Array<IRecordedInterviewQuestion>;
  defaultIntakeId: string;
  applyEnabled: boolean;
  partialApplyJourney?: boolean; // Defaults to false (full journey)
  documentDownloads: {
    enabled: boolean;
    errorMessage: string;
  };
  documentDownloadsActive: boolean;
  enableStudentCourseBlock?: boolean;
  tuitionPostDiscount?: boolean;
  conditionStatusInCourseDetails?: boolean;
  courseConditionsInCourseDetails?: boolean;
  multiOfferStudentFlow?: boolean;
  autoMoveToClosedLost?: boolean;
  autoMoveToCASIssued?: boolean;
  evidenceForOfferInCourseDetails?: boolean;
  pathwayStudentsEvidenceForOfferInCourseDetails?: boolean;
  courseDeletedDigestEmail?: boolean;
  university_code: string;
  university_name: string;
  universityEmail?: string;
  universitySecureEmail?: string;
  formatTypes: Array<IFormatType>;
  importFieldMappings: IFieldMappingList;
  sftpImportFieldMappings: IFieldMappingList;
  mappingTypes: IMappingType;
  setupTypes: Array<string>;
  sponsor: unknown;
  stages: Array<CoreStatusExtended>;
  moveToStages: Array<{
    key: CoreStatusExtended;
    label: string;
  }>;
  teamName: string;
  updatedAt?: number;
  // Only present on pathway provider configs. Not stored on regular uni configs.
  pathwayIds?: Array<IPathwayId>;
  partners?: Array<string>;
  immigrationProviders?: Array<string>;
  tags?: Array<ITag>;
  relatedPathways?: Array<string>;
  // .relatedPathwaySettings is a field added for direct uni configs.
  // It displays partial information from pathway provider config.
  // Computed at read time by getUniConfig when withRelatedPathway=true. Not stored in DynamoDB.
  relatedPathwaySettings?: IPathwayId;
  contactUniEmails?: Array<string>;
  pathwayReviewNotificationEmails?: Array<string>;
  conflictReportEmails?: Array<string>;
  dataEscrowConfig?: {
    type: string;
    config: unknown;
  };
  staffMfaEnabled?: boolean;
  studentsMfaEnabled?: boolean;
  // Used for agents only
  termsAndConditionsVersionDate?: number;
  allowedAliases?: string[];
  singleSignOn?: ISingleSignOn;
  sits?: ISITS;
  oracle?: IOracle;
  ellucian?: IEllucian;
  offerEnabled?: boolean;
  manualVisaSupportEnabled?: boolean;
  hasAgentOnlyDocs?: boolean;
  readyForCASEmailStatus?: ConfigEmailStatus;
  deferralNotificationEmailStatus?: ConfigEmailStatus;
  newInterviewRequiredStatus?: ConfigEmailStatus;
  CalendlyConfig?: {
    apiKey?: string;
    isCalendlyEnabled?: boolean;
  };
  resolveConflictsModalText?: string;
  personalInfoPage5Uneditable?: boolean; // There is no config UI for this value, it needs to be manually set - STM-287
  depositUneditable?: boolean; // There is no config UI for this value, it needs to be manually set - STM-289
  recordedInterview?: {
    enabled: boolean;
    autoGen: boolean;
    jobId: string;
  };
  isPathway?: boolean;
  processedByUni?: boolean; // When pathway provider students are processed by uni
  importHashOverride?: boolean; // Ignore the importHash check on import and always reimport the student again
  insightsAccessEnabled?: InsightsAccessLevel;
  relatedUnis?: Array<string>;
  reminderNotifications: ReminderNotificationsConfig;
  documentRetentionType?: DocumentRetentionType;
  termsAndConditionsUrl?: string;
  applyExternalCourseIdPattern?: string;
  additionalProgrammeEnabled?: boolean;
  genderEnabled?: boolean;
  hideUcasId?: boolean;
  apply?: {
    studentNumberEnabled?: boolean;
    importToCasShield?: {
      enabled?: boolean;
      unconditionalOfferAccepted?: boolean;
      paymentTasksApproved?: boolean;
    };
  };
}

export interface ReminderNotificationsEmail {
  /**
   * A number to show how many days will be between an email is re-sent
   */
  repeat?: number;
  /**
   * Delay after the trigger for the first sending (in days).
   */
  initialDelay?: number;
}

export type ReminderNotificationsEmailKeys =
  | ApplicantReminderEmailTemplates
  | Exclude<
      EmailTemplates,
      | EmailTemplates.COURSE_DELETION_REPORT
      | EmailTemplates.PATHWAY_REVIEW_STUDENTS_FOR_UNI_REPORT
      | EmailTemplates.CONFLICT_EXCEPTION_REPORT
    >;
export interface ReminderNotificationsConfig {
  emails: Record<ReminderNotificationsEmailKeys, ReminderNotificationsEmail>;
}

export enum DocumentRetentionType {
  STANDARD = 'Standard Retention',
  UKVI = 'UK VI Retention',
}

export interface IImmigrationProviderConfig extends Omit<IUniConfig, 'stages' | 'moveToStages'> {
  stages: Array<CoreStatus>;
  moveToStages: Array<{
    key: CoreStatus;
    label: string;
  }>;
}

export interface SamlRoleMapping {
  groupString: string;
  role: IUserRole;
  insightsAccess?: boolean;
  pathwayAccess?: PathwayAccess;
}

export interface ISingleSignOn {
  samlEnabled?: boolean;
  samlRoleMapping?: Array<SamlRoleMapping>;
  defaultRole?: IUserRole;
  defaultInsights?: boolean;
  defaultPathwayAccess?: PathwayAccess;
  samlOfficeMapping?: {
    [name: string]: string;
  };
  idpMetadataUrl?: string;
  attributeMapping?: {
    name: string;
    'custom:SSOgroups'?: string;
    given_name: string;
    family_name: string;
    email: string;
  };
  cognitoDomain?: string;
  cognitoProviderName?: string;
  redirectUrl?: string;
  idpLogout?: boolean;
  noRoleAssignedMsg?: string;
}

export enum SitsAuthMethod {
  Basic = 'Basic',
  OAuth = 'OAuth',
  HeaderToken = 'Header/Token',
}

export enum EllucianAuthMethod {
  Basic = 'Basic',
  OAuth = 'OAuth',
  SecretToken = 'SecretToken',
}

export interface ISITS {
  sitsEnabled?: boolean;
  sitsApplyEnabled?: boolean;
  sitsAuthMethod?: SitsAuthMethod;
  vcrRequired?: boolean;
  sitsUrl?: string;
  sitsUrlBasePath?: string;
  sitsOAuthUrl?: string;
  sitsUserName?: string;
  sitsPassword?: string;
  sitsClientId?: string;
  sitsSecret?: string;
  sitsOAuthScope?: string;
  sitsHeaderName?: string;
  sitsHeaderToken?: string;
  newDocEndpoint?: boolean;
  newDocType?: boolean; // Only used when newDocEndpoint is true
  newVCREndpoint?: boolean;
  useApplicantApiEndpoint?: boolean;
  config: {
    [intake: string]: SITSConfig;
  };
}

export interface IOracle {
  oracleEnabled: boolean;
  oracleUrl: string;
  oracleUserName?: string;
  oraclePassword?: string;
  oracleValueMappings?: Array<IEllucianValueMappings>;
}

export interface IEllucianValueMappings {
  fieldType: string;
  values: Array<{
    source: string;
    target: string;
  }>;
}

export interface Feature {
  label: string;
  enable: boolean;
}

export interface FeaturesState {
  [key: string]: Feature;
}

export interface IEllucian {
  ellucianEnabled: boolean;
  ellucianAuthMethod: string;
  ellucianUrl?: string;
  ellucianUrlBasePath?: string;
  ellucianUserName?: string;
  ellucianPassword?: string;
  ellucianClientId?: string;
  ellucianSecret?: string;
  ellucianToken?: string;
  ellucianOAuthScope?: string;
  ellucianValueMappings?: Array<IEllucianValueMappings>;
  ellucianFeatures: FeaturesState;
}

export interface SITSConfig {
  sitsDocMapping?: {
    [name: string]: { enrolyDocType: string; label?: string };
  };
  sitsVCRMapping?: {
    [name: string]: Record<string, string>;
  };
  sitsDocOutMapping?: {
    [name: string]: string;
  };
  sitsFieldMapping: {
    studentNo: string;
    email: string;
    familyName: string;
    givenName: string;
    middleName1: string;
    middleName2: string;
    nationality: string;
    gender: string;
    countryOfBirth: string;
    placeOfBirth: string;
    dob: string;
    phone: string;
    ucasId: string;
    passportNumber: string;
    passportSeqn: string;
    passportName: string;
    passportIssueDate: string;
    passportPlaceOfIssue: string;
    passportExpiry: string;
    passportNationality: string;
    passportBirthCountry: string;
    passportVisSeqn: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    county: string;
    country: string;
    postcode: string;
    englishLevelAttained: string;
    workPlacmentPerc: string;
    workPlacementText: string;
    workPlacementAddressLine1: string;
    workPlacementAddressLine2: string;
    workPlacementAddressLine3: string;
    workPlacementCity: string;
    workPlacementCounty: string;
    workPlacementPostcode: string;
    workPlacementCountry: string;
    qualificationEndDate: string;
    userDefined1: string;
    userDefined2: string;
    userDefined3: string;
    userDefined4: string;
    userDefined5: string;
    userDefined6: string;
    userDefined7: string;
    userDefined8: string;
    userDefined9: string;
    userDefined10: string;
    userDefined11: string;
    userDefined12: string;
    userDefined13: string;
    userDefined14: string;
    userDefined15: string;
    userDefined16: string;
    userDefined17: string;
    userDefined18: string;
    userDefined19: string;
    userDefined20: string;
    seltSpeaking: string;
    seltListening: string;
    seltReading: string;
    seltWriting: string;
    englishTestProvider: string;
    prevUKStudy: string;
    previousCourseLevel: string;
    courseProgression: string;
    progressionText: string;
    isSELTReqd: string;
    reasonSELTNotReqd: string;
    admissionsOffice: string;
    overseasHigherEducationInstitutionDetails: string;
    sponsorName: string;
    quals: string;
    offers: string;

    offerLevelFields: {
      externalCasStatus: string;
      academicYear: string;
      accomFeePaidToDate: string;
      agentName: string;
      apfSeqn: string;
      capSeqn: string;
      conditional: string;
      courseCode: string;
      courseFeeFirstYear: string;
      courseFeePaidToDate: string;
      courses: string;
      docsUsedToObtainOffer: string;
      endDate: string;
      hoursPerWeek: string;
      innerLondon: string;
      isATASRequired: string;
      isDeclined: string;
      isFullTime: string;
      isWorkPlacement: string;
      lastAcceptanceDate: string;
      previousCourseLevel: string;
      level: string;
      month: string;
      secondaryLevel: string;
      startDate: string;
      studyAddressLine1: string;
      studyAddressLine2: string;
      studyAddressLine3: string;
      studyCity: string;
      studyCountry: string;
      studyCounty: string;
      studyPostcode: string;
      title: string;
      courseScholarshipAmount1?: string;
      courseScholarshipTitle1?: string;
      uniStatus: string;
      faculty: string;
      branchOffice?: string;
      counsellorEmail?: string;
      counsellorName?: string;
      agentFamilyName?: string;
      agentGivenName?: string;
      branchAddressLine1?: string;
      branchAddressLine2?: string;
      branchAddressLine3?: string;
      branchCity?: string;
      branchCountry?: string;
      branchPostcode?: string;
      // This ensures all configurable offer fields are listed
      // and type checked:
    } & Record<SharedOfferFields, string>;
    courseLevelFields: {
      cah3: string;
      id: string;
      title: string;
      isWorkPlacement?: string;
      courseScholarshipAmount1?: string;
      courseScholarshipTitle1?: string;
      qual_hecos1: string;
      qual_hecos2: string;
      qual_hecos3: string;
      qual_hecos4: string;
      qual_hecos5: string;
      qual_proportion1: string;
      qual_proportion2: string;
      qual_proportion3: string;
      qual_proportion4: string;
      qual_proportion5: string;
      qual_cat: string;
      // This ensures all configurable course fields are listed:
    } & Record<SharedCourseFields, string>;
    // This ensure all applicant fields are listed:
  } & Partial<Record<keyof IImportData, string>>;

  sponsorProvideAccom?: boolean;
  fy1CourseFeeIncludeAccom?: boolean;
  fy1BoardingFees?: number;
  accomFee?: number;
  sponsorLicenceNo: string;
  ohei: {
    [name: string]: {
      name: string;
      addressLine1: string;
      addressLine2: string;
      addressLine3: string;
      city: string;
      county: string;
      country: string;
      postCode: string;
    };
  };
  partner: {
    [name: string]: {
      name: string;
      licenceNo: string;
      addressLine1: string;
      addressLine2: string;
      addressLine3: string;
      city: string;
      county: string;
      country: string;
      postCode: string;
    };
  };
  evidenceDocs: {
    prefix: string;
    suffix: string;
    linePrefix: string;
    fieldArray: Array<string>;
    fieldSeparator: Array<string>;
    lineSuffix: string;
  };
  innerLondon: boolean;
  hasSELTSInQuals?: boolean;
  sitsDataOut: {
    courseFeeDeposit: boolean;
  };
}

export interface IUniversityKey {
  key: string;
  name: string;
}

export enum InsightsAccessLevel {
  NO = '0',
  YES_STANDARD = '1',
  YES_STANDARD_AND_HECOS = '2',
}

type SITSFieldMapping = SITSConfig['sitsFieldMapping'];
type OfferLevelFields = SITSFieldMapping['offerLevelFields'];

type SharedOfferFields = Exclude<
  Extract<keyof SITSFieldMapping, keyof IImportOffer>,
  'applicationId' // Apply-specific field only
>;
type SharedCourseFields =
  | Extract<keyof OfferLevelFields, keyof IImportCourse>
  | Extract<keyof SITSFieldMapping, keyof IImportCourse>;

export interface ListApplyCoursesParams {
  uni: string;
  intakeId: string;
  page: number;
  limit: number;
  searchOn?:
    | 'name'
    | 'shortName'
    | 'id'
    | 'hecos'
    | 'campus'
    | 'faculty'
    | 'department'
    | 'category'
    | 'award'
    | 'UCASApplyCode'
    | 'UCASFacultyCode';
  searchValue?: string;
}
